import React, { useEffect, useState } from "react";
import MainRouter from "./components/ReusableComponents/MainRouter";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useDispatch, useSelector } from "react-redux";
import HubSpotChat from "./components/HubSpotChat";
import Chat from "./Chat";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";

import "./App.scss";
import "./neonMode.scss";
import "./light-new.scss";
import { Toaster } from "react-hot-toast";
import zIndex from "@mui/material/styles/zIndex";
import { AcctokenD } from "./store/actions";
import { generateDeviceId, useDeviceDetect } from "./utilities/string";
import AlphaPocket from "./assets/images/popups/AlphaPocket.png";
import DOBWarningModal from "./components/DOBWarningModal";
import { baseUrl } from "./Axios";
import axios from "axios";
import { message } from "antd";
import { getUserProfile } from "./store/reducers/userProfileReducer";

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: "light",
  },
});

function App() {
  // Get the selected theme from Redux state
  const theme = useSelector((state) => state.theme);
  const ACCtoken = useSelector((state) => state.ACCtoken);
  const dispatch = useDispatch();
  let deviceId = localStorage.getItem("deviceId");
  const { date_of_birth, User_id } = useSelector((state) => state.user);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [loader, setLoader] = useState(false);
  useEffect(() => {
    if (
      User_id?.id &&
      ACCtoken &&
      !date_of_birth
    ) {
      setShowWarningPopup(true);
    }

    if(!ACCtoken) setShowWarningPopup(false);
  }, [ACCtoken, User_id?.id]);

  if (!deviceId) {
    deviceId = generateDeviceId(20);
    localStorage.setItem("deviceId", deviceId);
  }

  async function onOK(dob) {
    const config = {
      headers: {
        Authorization: `Bearer ${ACCtoken}`,
      },
    };
    try {
      setLoader(true);
      const formData = new FormData();
      formData.append("date_of_birth", dob?.format("YYYY-MM-DD"));
      const data = await axios.post(`${baseUrl}/v3/user/profile/create/`, formData, config).then(() => dispatch(getUserProfile()));
      message.success("Profile updated successfully!");
      setShowWarningPopup(false);
      setLoader(false);
    } catch (error) {
      setShowWarningPopup(false);
      setLoader(false);
    } finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    const handler = (event) => {
      const { hostname } = window.location;
      let validUrl = "http://localhost:3000";

      // UAT environment
      if (hostname === "user-uat.alphacapitalgroup.uk") {
        console.log("Running in UAT environment");
        validUrl = "https://admin-uat.alphacapitalgroup.uk";
      }

      // DEV/QA environment
      if (hostname === "user-dev.alphacapitalgroup.uk") {
        console.log("Running in DEV/QA environment");
        validUrl = "https://admin-dev.alphacapitalgroup.uk";
      }

      // Production environment
      if (hostname === "app.alphacapitalgroup.uk") {
        console.log("Running in Production environment");
        validUrl = "https://super.alphacapitalgroup.uk";
      }

      console.log("event.origin:", event?.origin);

      // Security check
      if (event.origin !== validUrl) return;
      // ✅ Security check
      if (event?.origin !== validUrl) return;

      const token = event?.data?.token;
      if (token) {
        dispatch(AcctokenD(token));
      }

      window.removeEventListener("load", notifyParentReady);
      window.removeEventListener("message", handler);
    };

    const notifyParentReady = () => {
      if (window.opener) {
        // Determine the expected parent origin dynamically
        const parentOrigin =
          window.location.hostname === "user-uat.alphacapitalgroup.uk"
            ? "https://admin-uat.alphacapitalgroup.uk"
            : window.location.hostname === "user-dev.alphacapitalgroup.uk"
              ? "https://admin-dev.alphacapitalgroup.uk"
              : window.location.hostname === "app.alphacapitalgroup.uk"
                ? "https://super.alphacapitalgroup.uk"
                : "http://localhost:3000";

        console.log("Notifying parent, origin:", parentOrigin);
        window.opener.postMessage({ ready: true }, parentOrigin);
      }
    };

    // Tell parent we’re ready once page finishes loading
    window.addEventListener("load", notifyParentReady);

    // Listen for token messages
    window.addEventListener("message", handler);

    // ✅ Cleanup listeners on unmount
    return () => {
      window.removeEventListener("message", handler);
      window.removeEventListener("load", notifyParentReady);
    };
  }, [dispatch]);

  // Effect to handle context menu
  useEffect(() => {
    const handleContextmenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextmenu);
    return function cleanup() {
      document.removeEventListener("contextmenu", handleContextmenu);
    };
  }, []);

  // Effect to apply the selected theme
  useEffect(() => {
    // Remove existing theme classes
    document.body.classList.remove("light", "light-new");
    // Add class for the selected theme
    document.body.classList.add(theme.theme);
  }, [theme]);

  // remove all console logs
  // console.log = function () {};
  const deviceType = useDeviceDetect();
  const [isAlphaPocketOpen, setIsAlphaPocketOpen] = useState(false);

  const storeLinks = {
    ios: "https://apps.apple.com/in/app/alpha-capital-group/id6745693120",
    android: "https://play.google.com/store/apps/details?id=uk.acg.android",
  };

  useEffect(() => {
    if (
      deviceType &&
      (deviceType.toLowerCase() === "android" ||
        deviceType.toLowerCase() === "ios")
    ) {
      setIsAlphaPocketOpen(true);
    }
  }, [deviceType]);

  return (
    <div className="App">
      {/* ThemeProvider to apply the dark theme */}
      <ThemeProvider theme={darkTheme}>
        {/* Apply CSS baseline */}
        <CssBaseline />
        {/* Render Chat component */}
        <Chat />
        {/* Render MainRouter component */}
        <MainRouter />
        <Toaster
          style={{ zIndex: "100000000" }}
          position="top-right"
          reverseOrder={false}
        />
        <Dialog
          open={isAlphaPocketOpen}
          onClose={() => setIsAlphaPocketOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              maxWidth: 600,
              width: "100%",
              margin: 2,
              borderRadius: 2,
              overflow: "hidden",
            },
          }}
        >
          <DialogContent
            sx={{
              p: 0,
              position: "relative",
            }}
          >
            <IconButton
              aria-label="Close AlphaPocket"
              onClick={() => setIsAlphaPocketOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "rgba(0,0,0,0.4)",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.6)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {storeLinks[deviceType] ? (
              <Box
                component="a"
                href={storeLinks[deviceType]}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  textDecoration: "none",
                }}
              >
                <Box
                  component="img"
                  src={AlphaPocket}
                  alt="AlphaPocket"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    maxWidth: 600,
                    margin: "0 auto",
                  }}
                />
              </Box>
            ) : (
              <Box
                component="img"
                src={AlphaPocket}
                alt="AlphaPocket"
                sx={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  maxWidth: 600,
                  margin: "0 auto",
                }}
              />
            )}
          </DialogContent>
        </Dialog>
        <DOBWarningModal
          isOpen={showWarningPopup}
          onOK={onOK}
          text={"Please enter your date of birth to continue."}
          loader={loader}
        />
      </ThemeProvider>
      {/* Other components */}
      {/* <Notification /> */}
      {/* <HubSpotChat/> */}
    </div>
  );
}

export default App;
